import { REALTIME } from "../config/global.configuration";
import { type CompactTimeline } from "../model/realtime/CompactTimeline";
import type { StopAnchorWire, TripDelayWire } from "../types/dtos/realtime-api/realtime.dto";

export interface VehicleRenderPosition {
    "lat":     number;
    "lon":     number;
    "bearing": number;
}

interface VehicleState {
    "lastPointIndex":     number;
    "lastScheduledTimeS": number | null;
    "lastRealTimeS":      number | null;
}

/**
 * Per-frame interpolation engine.
 * For each visible vehicle, resolves real time -> scheduled time (applying delays),
 * then interpolates position along the prebuilt timeline.
 * Cost: O(1) amortized per vehicle per frame (forward-only scan).
 */
export class InterpolationEngine {

    private vehicleStates: Map<string, VehicleState> = new Map();

    /**
     * Get the interpolated position of a vehicle at a given real-world timestamp.
     *
     * @param tripId Trip identifier used to keep interpolation state.
     * @param timeline Prebuilt {@link CompactTimeline} for that trip (columnar buffers).
     * @param delay Optional realtime delay payload.
     * @param stops Stop anchors used for real-to-scheduled time conversion.
     * @param realTimestampMs Current real-world timestamp in milliseconds.
     * @returns Interpolated render position, or null when out of range.
     */
    getPosition(
        tripId: string,
        timeline: CompactTimeline,
        delay: TripDelayWire | undefined,
        stops: StopAnchorWire[],
        realTimestampMs: number,
    ): VehicleRenderPosition | null {
        if ( timeline.length < 2 ) {
            return null;
        }

        const realTimeS = timestampMsToSecondsSinceMidnight( realTimestampMs );
        const rawScheduledTime = this.realToScheduledTime( realTimeS, delay, stops );

        if ( rawScheduledTime === null ) {
            return null;
        }

        const scheduledTime = rawScheduledTime;

        const timelineStartS = timeline.timestampS[0];
        const timelineEndS = timeline.timestampS[timeline.length - 1];
        const clampedTargetScheduledTime = Math.min( timelineEndS, Math.max( timelineStartS, scheduledTime ) );

        let state = this.vehicleStates.get( tripId );
        if ( !state ) {
            state = {
                "lastPointIndex":     0,
                "lastScheduledTimeS": null,
                "lastRealTimeS":      null,
            };
            this.vehicleStates.set( tripId, state );
        }

        // Bus hasn't departed yet: don't render it at all.
        if ( scheduledTime < timelineStartS ) {
            state.lastScheduledTimeS = timelineStartS;
            state.lastRealTimeS = realTimeS;
            state.lastPointIndex = 0;

            return null;
        }

        const constrainedScheduledTime = this.computeConstrainedScheduledTime( state, realTimeS, clampedTargetScheduledTime );

        let i = state.lastPointIndex;
        if ( i >= timeline.length - 1 ) {
            i = 0;
        }

        // Forward-only scan
        while ( i < timeline.length - 1 && constrainedScheduledTime > timeline.timestampS[i + 1] ) {
            i++;
        }

        if ( i >= timeline.length - 1 ) {
            return null;
        }

        state.lastPointIndex = i;

        const t1 = timeline.timestampS[i];
        const t2 = timeline.timestampS[i + 1];
        const dt = t2 - t1;
        const ratio = dt > 0 ? ( constrainedScheduledTime - t1 ) / dt : 0;

        // Hot path: direct column reads on CompactTimeline (no per-point getters).
        const lat = timeline.lat[i] + ( timeline.lat[i + 1] - timeline.lat[i] ) * ratio;
        const lon = timeline.lon[i] + ( timeline.lon[i + 1] - timeline.lon[i] ) * ratio;
        const bearing = interpolateBearing(
            timeline.bearing[i],
            timeline.bearing[i + 1],
            lat,
            lon,
            timeline.lat[i + 1],
            timeline.lon[i + 1],
        );

        return {
            lat,
            lon,
            bearing,
        };
    }

    /**
     * Resolves position along the timeline without smoothing state (no per-trip cache).
     * Clamps scheduled time to the timeline span — used when the main path returns null
     * but a map overlay still needs a leading coordinate (e.g. clicked bus path).
     *
     * @param timeline Prebuilt columnar timeline.
     * @param delay Optional realtime delay payload.
     * @param stops Stop anchors for real-to-scheduled conversion.
     * @param realTimestampMs Current real-world timestamp in milliseconds.
     * @returns Interpolated position, or null when the timeline is too short or time cannot be resolved.
     */
    getPositionClampedForPath(
        timeline: CompactTimeline,
        delay: TripDelayWire | undefined,
        stops: StopAnchorWire[],
        realTimestampMs: number,
    ): VehicleRenderPosition | null {
        if ( timeline.length < 2 ) {
            return null;
        }

        const realTimeS = timestampMsToSecondsSinceMidnight( realTimestampMs );
        const rawScheduledTime = this.realToScheduledTime( realTimeS, delay, stops );

        if ( rawScheduledTime === null ) {
            return null;
        }

        const timelineStartS = timeline.timestampS[0];
        const timelineEndS = timeline.timestampS[timeline.length - 1];
        const scheduledTime = Math.min( timelineEndS, Math.max( timelineStartS, rawScheduledTime ) );

        let i = 0;
        while ( i < timeline.length - 1 && scheduledTime > timeline.timestampS[i + 1] ) {
            i++;
        }

        if ( i >= timeline.length - 1 ) {
            const last = timeline.length - 1;

            return {
                "lat":     timeline.lat[last],
                "lon":     timeline.lon[last],
                "bearing": timeline.bearing[last],
            };
        }

        const t1 = timeline.timestampS[i];
        const t2 = timeline.timestampS[i + 1];
        const dt = t2 - t1;
        const ratio = dt > 0 ? ( scheduledTime - t1 ) / dt : 0;

        const lat = timeline.lat[i] + ( timeline.lat[i + 1] - timeline.lat[i] ) * ratio;
        const lon = timeline.lon[i] + ( timeline.lon[i + 1] - timeline.lon[i] ) * ratio;
        const bearing = interpolateBearing(
            timeline.bearing[i],
            timeline.bearing[i + 1],
            lat,
            lon,
            timeline.lat[i + 1],
            timeline.lon[i + 1],
        );

        return {
            lat,
            lon,
            bearing,
        };
    }

    /**
     * Computes a monotonic visual scheduled-time with a single continuous speed
     * curve: speedRatio = clamp(1 + gap / catchupWindow, minSpeed, maxSpeed).
     *
     * - gap > 0 (target ahead): bus accelerates to converge within ~catchupWindow.
     * - gap < 0 (target behind): bus decelerates smoothly, minimum 0.1x real speed.
     * - gap ≈ 0: bus moves at ~1x real speed.
     *
     * No discontinuity at the crossover point, which eliminates the
     * accelerate-then-brake oscillation when the target fluctuates.
     */
    private computeConstrainedScheduledTime(
        state: VehicleState,
        realTimeS: number,
        targetScheduledTimeS: number,
    ): number {
        const previousScheduled = state.lastScheduledTimeS;
        const previousReal = state.lastRealTimeS;

        if ( previousScheduled === null || previousReal === null ) {
            state.lastScheduledTimeS = targetScheduledTimeS;
            state.lastRealTimeS = realTimeS;

            return targetScheduledTimeS;
        }

        const realDeltaS = Math.max( 0, realTimeS - previousReal );
        if ( realDeltaS <= 0 ) {
            state.lastRealTimeS = realTimeS;

            return previousScheduled;
        }

        /*
         * After a long pause (tab hidden, app backgrounded), the accumulated
         * delta would be huge and produce a massive overshoot.  Treat any gap
         * longer than 5 seconds as a re-initialization: jump straight to target.
         */
        if ( realDeltaS > 5 ) {
            state.lastScheduledTimeS = targetScheduledTimeS;
            state.lastRealTimeS = realTimeS;

            return targetScheduledTimeS;
        }

        const catchupWindowS = Math.max( 1, REALTIME.delaySmoothingMaxMs / 1000 );
        const MIN_SPEED = 0.1;
        const MAX_SPEED = 10;

        const gapS = targetScheduledTimeS - previousScheduled;
        const speedRatio = Math.min( MAX_SPEED, Math.max( MIN_SPEED, 1 + gapS / catchupWindowS ) );

        const advanceS = realDeltaS * speedRatio;
        const nextScheduledTime = previousScheduled + advanceS;

        state.lastScheduledTimeS = nextScheduledTime;
        state.lastRealTimeS = realTimeS;

        return nextScheduledTime;
    }

    /**
     * Convert real time to scheduled time using GTFS-RT delay data.
     *
     * Builds a monotonic sequence of (predictedRealDep, scheduledDep) anchors
     * from each stop, then linearly interpolates.  This avoids the gaps that
     * the previous interval-based approach had (dwell time, inverted intervals
     * when per-stop delays vary a lot) and guarantees a continuous mapping.
     *
     * @param realTimeS Real-world seconds since midnight.
     * @param delay Delay payload for the trip.
     * @param stops Ordered stop anchors of the trip.
     * @returns Estimated scheduled time in seconds since midnight, or null if unresolved.
     */
    private realToScheduledTime(
        realTimeS: number,
        delay: TripDelayWire | undefined,
        stops: StopAnchorWire[],
    ): number | null {
        const globalDelay = delay?.global ?? 0;

        if ( stops.length < 2 ) {
            return realTimeS - globalDelay;
        }

        // Build monotonic anchors: (predictedReal, scheduled) using departure times.
        const anchors = this.buildPredictedAnchors( stops, delay, globalDelay );

        if ( anchors.length < 2 ) {
            return realTimeS - globalDelay;
        }

        // Before first anchor: extrapolate with first stop's delay.
        if ( realTimeS <= anchors[0].real ) {
            return realTimeS - ( anchors[0].real - anchors[0].sched );
        }

        // After last anchor: extrapolate with last stop's delay.
        const last = anchors[anchors.length - 1];
        if ( realTimeS >= last.real ) {
            return realTimeS - ( last.real - last.sched );
        }

        // Find enclosing pair and linearly interpolate.
        for ( let k = 0; k < anchors.length - 1; k++ ) {
            if ( realTimeS >= anchors[k].real && realTimeS <= anchors[k + 1].real ) {
                const realSpan = anchors[k + 1].real - anchors[k].real;
                if ( realSpan <= 0 ) {
                    return anchors[k].sched;
                }
                const ratio = ( realTimeS - anchors[k].real ) / realSpan;

                return anchors[k].sched + ratio * ( anchors[k + 1].sched - anchors[k].sched );
            }
        }

        return realTimeS - globalDelay;
    }

    /**
     * Builds a monotonically increasing array of (predictedRealTime, scheduledTime)
     * anchors from stops and their delays.  Non-monotonic predicted times are
     * skipped so that the mapping remains a proper function.
     */
    private buildPredictedAnchors(
        stops: StopAnchorWire[],
        delay: TripDelayWire | undefined,
        globalDelay: number,
    ): {
        "real":  number;
        "sched": number;
    }[] {
        const anchors: {
            "real":  number;
            "sched": number;
        }[] = [];

        let lastReal = -Infinity;
        for ( const stop of stops ) {
            const stopDelay =  delay !== undefined && delay.stops.length > 0
                ?  getDelayForStop( delay, stop.seq ) ?? globalDelay
                : globalDelay;
            const predictedReal = stop.dep + stopDelay;

            if ( predictedReal > lastReal ) {
                anchors.push( {
                    "real":  predictedReal,
                    "sched": stop.dep,
                } );
                lastReal = predictedReal;
            }
        }

        return anchors;
    }

    /**
     * Remove tracking state for a trip that is no longer visible.
     *
     * @param tripId Trip identifier to remove from interpolation state.
     * @returns Nothing.
     */
    removeVehicle( tripId: string ): void {
        this.vehicleStates.delete( tripId );
    }

    /**
     * Clear all vehicle states.
     *
     * @returns Nothing.
     */
    clear(): void {
        this.vehicleStates.clear();
    }

    /**
     * Returns how many trips currently retain interpolation scan state.
     *
     * @returns Count of tracked trip identifiers.
     */
    getTrackedVehicleCount(): number {
        return this.vehicleStates.size;
    }

}

/**
 * Returns the delay override for a specific stop sequence if present.
 *
 * @param delay Trip delay payload.
 * @param stopSeq Stop sequence number.
 * @returns Delay in seconds for the stop, or undefined if no override exists.
 */
function getDelayForStop( delay: TripDelayWire, stopSeq: number ): number | undefined {
    for ( const [ seq, d ] of delay.stops ) {
        if ( seq === stopSeq ) {
            return d;
        }
    }

    return undefined;
}

/**
 * Interpolates vehicle bearing near turn points to smooth heading transitions.
 *
 * @param p1 Previous timeline point.
 * @param p2 Next timeline point.
 * @param currentLat Current interpolated latitude.
 * @param currentLon Current interpolated longitude.
 * @returns Smoothed bearing in degrees.
 */
function interpolateBearing(
    bearing1: number,
    bearing2: number,
    currentLat: number,
    currentLon: number,
    nextLat: number,
    nextLon: number,
): number {
    const remainingDist = haversineDistanceFast( currentLat, currentLon, nextLat, nextLon );

    if ( remainingDist > REALTIME.turningThresholdM ) {
        return bearing1;
    }

    const turnProgress = ( REALTIME.turningThresholdM - remainingDist ) / REALTIME.turningThresholdM;

    return lerpAngle( bearing1, bearing2, turnProgress );
}

/**
 * Linearly interpolates two angles along the shortest circular path.
 *
 * @param a1 Start angle in degrees.
 * @param a2 End angle in degrees.
 * @param t Interpolation ratio between 0 and 1.
 * @returns Interpolated angle in degrees.
 */
function lerpAngle( a1: number, a2: number, t: number ): number {
    let delta = ( ( a2 - a1 + 180 ) % 360 ) - 180;
    if ( delta < -180 ) {
        delta += 360;
    }

    return a1 + delta * t;
}

/**
 * Computes approximate geodesic distance between two coordinates in meters.
 *
 * @param lat1 Latitude of the first point.
 * @param lon1 Longitude of the first point.
 * @param lat2 Latitude of the second point.
 * @param lon2 Longitude of the second point.
 * @returns Distance in meters.
 */
function haversineDistanceFast( lat1: number, lon1: number, lat2: number, lon2: number ): number {
    const R = 6371000;
    const dLat = ( lat2 - lat1 ) * Math.PI / 180;
    const dLon = ( lon2 - lon1 ) * Math.PI / 180;
    const a =
        Math.sin( dLat / 2 ) * Math.sin( dLat / 2 ) +
        Math.cos( lat1 * Math.PI / 180 ) * Math.cos( lat2 * Math.PI / 180 ) *
        Math.sin( dLon / 2 ) * Math.sin( dLon / 2 );

    return R * 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );
}

/**
 * Converts epoch milliseconds to seconds since midnight (0..86399).
 *
 * @param tsMs Epoch timestamp in milliseconds.
 * @returns Seconds since midnight.
 */
function timestampMsToSecondsSinceMidnight( tsMs: number ): number {
    const d = new Date( tsMs );

    return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds() + d.getMilliseconds() / 1000;
}
